/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import React from "react";
import type { ArticleComponentsFragment, CosmosCalloutCardFragment } from "@cosmos/hygraph-schema";

import {
	Animate,
	ArticleChildrenWrap,
	ArticleContainer,
	AssetCarouselSection,
	AssetGallerySection,
	ButtonList,
	Caption,
	ContentLargeImageSection,
	CosmosAsset,
	CosmosCompare,
	CosmosGitHubGist,
	CosmosRichText,
	CosmosEmbed,
	DividerSection,
	IconLinkGroup,
	Interview,
	LogoMarquee,
	LogoMultiCardsSection,
	NewsMediaCardsSection,
	SectionHeading,
	SlimContactCard,
	UserProfile,
	Wrap,
	Quote,
	CosmosCalloutCard,
	TitleDescStack,
	SimpleCtaSection,
} from "@components/elements";
import { css, cx } from "@panda/css";
import { useLocation } from "@epic-mw/app-context";
import { useCosmosComponentContext, HeadingLevelContext } from "@components/context";
import { normalizeRichTextHeadingLevels } from "@components/elements/atoms/text/text.utils";
import { articleMdStyles } from "@components/elements/atoms/container/ArticleChildrenWrap.atom";
import { isGameModeAllowedHref, withGameModeParam } from "@components/elements/atoms/rich-text/gameModeLinks";

/**
 * Every `href` value inside a fragment, at any depth.
 *
 * Deliberately generic rather than a per-`__typename` extractor: 11 of the 23
 * article component types carry hrefs, nested at different depths, and a
 * hand-written extractor per shape silently misses the next one the schema
 * adds. The generated Hygraph types name every link field `href`, so walking for
 * that key covers all of them and keeps covering them.
 */
const collectHrefs = (value: unknown, out: string[] = [], seen = new WeakSet<object>()): string[] => {
	if (value === null || typeof value !== "object") return out;

	// No depth cap. An earlier version bailed past depth 6, which failed *open*:
	// a link nested deeper than the cap was never inspected, so the component
	// rendered with a live anchor — the opposite direction from every other
	// decision here.
	//
	// The WeakSet replaces that cap. CMS fragments arrive as parsed JSON and so
	// can't contain a cycle, but hand-authored story stubs and test fixtures are
	// plain objects that can, and without a cap a single self-reference would
	// recurse until the stack blew rather than merely mis-gating one component.
	if (seen.has(value)) return out;
	seen.add(value);

	if (Array.isArray(value)) {
		for (const item of value) collectHrefs(item, out, seen);
		return out;
	}

	for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
		if (key === "href" && typeof nested === "string" && nested) out.push(nested);
		else collectHrefs(nested, out, seen);
	}
	return out;
};

/**
 * Copy of a fragment with every allowlisted `href` carrying `mode=game`, so
 * following a CTA keeps the destination in game mode the way the rich-text path
 * already does. Without this an allowlisted button landed the webview on a page
 * that rendered with nav and live links.
 *
 * Only called for components that already passed {@link isSafeInGameMode}, so any
 * href here is either allowlisted or a bare fragment; fragments are left alone.
 */
const withGameModeHrefs = <T,>(value: T, location: URL): T => {
	if (value === null || typeof value !== "object") return value;

	if (Array.isArray(value)) return value.map((item) => withGameModeHrefs(item, location)) as unknown as T;

	const out: Record<string, unknown> = {};
	for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
		if (key === "href" && typeof nested === "string" && nested && !nested.startsWith("#")) {
			out[key] = withGameModeParam(location, nested);
		} else {
			out[key] = withGameModeHrefs(nested, location);
		}
	}
	return out as T;
};

/**
 * May this component render in an in-game / console webview?
 *
 * Components whose links live in rich text carry no `href` field and are always
 * safe — `CosmosRichText` neutralises its own links. Components that emit their
 * own anchors render only when *every* href they would emit is allowlisted;
 * otherwise the article would show a live CTA on a page whose prose links were
 * just neutralised, with no nav to escape with.
 */
const isSafeInGameMode = (component: ArticleComponentsFragment, location: URL): boolean =>
	collectHrefs(component)
		// A bare same-page fragment can't move the webview, so it shouldn't
		// disqualify a component and silently drop authored copy.
		.filter((href) => !href.startsWith("#"))
		.every((href) => isGameModeAllowedHref(location, href));

const textEditorStyles = css({
	"& h1": {
		textStyle: "heading2XL",
	},
	"& h2": {
		textStyle: "headingXL",
	},
	"& h3": {
		textStyle: "headingLG",
	},
	"& h4": {
		textStyle: "headingMD",
	},
	"& h5": {
		textStyle: "headingSM",
	},
	"& h6": {
		textStyle: "eyebrowLG",
	},
	"& h1, & h2, & h3, & h4, & h5, & h6": {
		marginBlock: "eds.dimension.8",
		textWrap: "wrap",
	},
	"& hr": {
		marginBlockStart: "revert",
		marginBlockEnd: "revert",
	},
	"& img": {
		margin: "0 auto",
	},
});

const articleSectionStyles = css({
	paddingBlock: "eds.dimension.64",
	smDown: {
		paddingBlock: "eds.dimension.48",
	},
});
const simpleCtaStyles = cx(articleMdStyles, articleSectionStyles);

export interface ArticleComponentsSectionProps {
	articleComponents?: ArticleComponentsFragment[] | null;
	nAnimatedAsset?: number;
	/**
	 * `?mode=game` / `?mode=iosgame` — an in-game or console webview. Rich-text
	 * links are rewritten: allowlisted Epic paths keep the mode param, everything
	 * else is rendered as non-clickable text (see CosmosRichText.game.ts).
	 *
	 * Components that emit their own anchors instead of rich text are dropped
	 * unless every href they carry is allowlisted — see {@link isSafeInGameMode}.
	 */
	isGameMode?: boolean;
}

export const ArticleComponentsSection = ({
	articleComponents,
	nAnimatedAsset = 0,
	isGameMode,
}: ArticleComponentsSectionProps): React.JSX.Element | null => {
	const { LoadingComponent } = useCosmosComponentContext();
	const location = useLocation();
	if (!articleComponents?.length) return null;

	const richTextStyles = css({
		"&&": {
			textStyle: "paragraphMD",
		},
		"& h1": {
			color: "red",
			fontSize: "unset",
		},
	});

	let assetIdx = -1;
	return (
		// The article body sits under the hero's <h1>; shift its component headings
		// up one level (h3→h2, …, floored at h2) so they nest correctly instead of
		// jumping h1→h3. Visual size is variant-driven, so it's unchanged.
		<HeadingLevelContext.Provider value={{ offset: -1 }}>
			{articleComponents.map((rawComponent, i) => {
				assetIdx++;
				// Drop anything carrying a link this webview couldn't come back from.
				// Checked before the switch so a new component type is covered the
				// moment it lands, rather than when someone remembers to gate it.
				if (isGameMode && !isSafeInGameMode(rawComponent, location)) return null;
				// Survivors carry mode=game onward, matching the rich-text path.
				const component = isGameMode ? withGameModeHrefs(rawComponent, location) : rawComponent;
				switch (component.__typename) {
					case "CosmosElementAsset":
						return (
							<ArticleContainer size="md" key={`article-section-${i}`}>
								<Wrap
									condition={assetIdx < nAnimatedAsset}
									wrap={(children) => (
										<Animate delay={0.25} type="slideLeft">
											{children}
										</Animate>
									)}
								>
									<Caption caption={component?.caption}>
										<CosmosAsset
											asset={component?.articleComponentAsset}
											className={css({
												borderRadius: "eds.dimension.24",
												margin: "0 auto",
											})}
										/>
									</Caption>
								</Wrap>
							</ArticleContainer>
						);
					case "CosmosElementButtonList":
						return (
							<ArticleContainer key={`article-section-${i}`}>
								<ButtonList {...component} />
							</ArticleContainer>
						);
					case "CosmosElementCkEditorText":
						return (
							<ArticleContainer key={`article-section-${i}`} className={cx(textEditorStyles)}>
								<CosmosRichText
									html={normalizeRichTextHeadingLevels(component?.ckText)}
									className={richTextStyles}
									isGameMode={isGameMode}
								/>
							</ArticleContainer>
						);
					case "CosmosElementGithubGist":
						return (
							<ArticleContainer key={`article-section-${i}`}>
								<CosmosGitHubGist {...component} LoadingComponent={LoadingComponent} />
							</ArticleContainer>
						);
					case "CosmosElementIconLinkGroup":
						return (
							<ArticleContainer key={`article-section-${i}`}>
								<IconLinkGroup {...component} />
							</ArticleContainer>
						);
					case "CosmosElementInterview":
						return (
							<ArticleContainer key={`article-section-${i}`}>
								<Interview {...component} isGameMode={isGameMode} />
							</ArticleContainer>
						);
					case "CosmosElementRichText":
						return (
							<ArticleContainer key={`article-section-${i}`} className={cx(textEditorStyles)}>
								<CosmosRichText
									html={normalizeRichTextHeadingLevels(component?.text?.html)}
									className={richTextStyles}
									isGameMode={isGameMode}
								/>
							</ArticleContainer>
						);
					case "CosmosElementSectionHeading":
						return (
							<ArticleContainer size="md" key={`article-section-${i}`}>
								<SectionHeading {...component} />
							</ArticleContainer>
						);
					case "CosmosElementLicenseContactCard":
						return (
							<ArticleContainer size="md" key={`article-section-${i}`}>
								<SlimContactCard {...component} />
							</ArticleContainer>
						);
					case "CosmosElementVideoEmbed":
						return (
							<ArticleContainer size="md" key={`article-section-${i}`}>
								<CosmosEmbed {...component} />
							</ArticleContainer>
						);
					case "CosmosSectionAssetCarousel":
						return (
							<ArticleChildrenWrap size="md" key={`article-section-${i}`}>
								<AssetCarouselSection {...component} />
							</ArticleChildrenWrap>
						);
					case "CosmosSectionAssetGallery":
						return (
							<ArticleChildrenWrap size="md" key={`article-section-${i}`}>
								<AssetGallerySection {...component} />
							</ArticleChildrenWrap>
						);
					case "CosmosSectionContentLargeImage":
						return (
							<ContentLargeImageSection
								sectionContainerProps={{
									disablePaddingBlock: true,
									className: articleSectionStyles,
								}}
								key={`article-section-${i}`}
								{...component}
							/>
						);
					case "CosmosSectionLogoList":
						return <LogoMarquee key={`article-section-${i}`} className={css({ width: "100vw" })} {...component} />;
					case "CosmosSectionLogoMultiCard":
						return (
							<ArticleChildrenWrap
								size="md"
								className={css({ "& .section-container > .d_flex": { xxl: { columnGap: "eds.dimension.32" } } })}
								key={`article-section-${i}`}
							>
								<LogoMultiCardsSection {...component} />
							</ArticleChildrenWrap>
						);
					case "CosmosSectionNewsMediaCard":
						return (
							<ArticleChildrenWrap size="md" key={`article-section-${i}`}>
								<NewsMediaCardsSection {...component} />
							</ArticleChildrenWrap>
						);
					case "CosmosElementCompare":
						return (
							<ArticleContainer size="md" key={`article-section-${i}`}>
								<CosmosCompare {...component} />
							</ArticleContainer>
						);
					case "CosmosElementUserProfile":
						return (
							<ArticleContainer key={`article-section-${i}`}>
								<UserProfile {...component} />
							</ArticleContainer>
						);
					case "CosmosSectionDivider":
						return (
							<div className={css({ paddingBlock: "eds.dimension.32" })} key={`article-section-divider-${i}`}>
								<DividerSection {...component} />
							</div>
						);
					case "CosmosElementQuote":
						return (
							<ArticleContainer key={`article-section-${i}`}>
								<Quote {...component} />
							</ArticleContainer>
						);
					case "CosmosElementCalloutCard":
						return (
							<CosmosCalloutCard
								wholePage={articleComponents.length === 1}
								{...(component as CosmosCalloutCardFragment)}
								key={`article-section-${i}`}
							/>
						);
					case "CosmosElementTitleAndDesc":
						return (
							<ArticleContainer key={`article-section-${i}`}>
								<TitleDescStack
									stack={{ gap: "eds.dimension.16" }}
									title={{ ...component.title, variant: "headingXL" }}
									description={{ html: component.desc, variant: "paragraphMD", color: "eds.text.primary" }}
								/>
							</ArticleContainer>
						);
					case "CosmosSectionSimpleCta":
						return (
							<SimpleCtaSection
								{...component}
								key={`article-section-${i}`}
								sectionContainerProps={{
									disablePaddingBlock: true,
									className: simpleCtaStyles,
								}}
							/>
						);
					default:
						return null;
				}
			})}
		</HeadingLevelContext.Provider>
	);
};
